import { motion } from "framer-motion";
import { MapPin, Eye } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { MANIFESTO, MEDIA } from "@/data/content";

const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 sm:py-32 overflow-hidden">
    <div className="glow-blob w-[26rem] h-[26rem] bg-blue-600/10 top-20 -right-40" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        caption="The Manifesto"
        title={<>Brands don't go viral in boardrooms. <span className="text-gradient">They go viral on the street.</span></>}
        description="Brandify helps brands connect directly with people through street activations, public engagement, product sampling, giveaways, games, influencer collaborations, and professional content creation. Every campaign is transformed into engaging social media content that builds trust and increases brand awareness."
        testId="about-heading"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        {MANIFESTO.map((ch, i) => (
          <Reveal key={ch.number} delay={i * 0.15}>
            <div className="glass rounded-2xl p-8 h-full card-glow-hover" data-testid={`manifesto-chapter-${ch.number}`}>
              <span className="font-display text-5xl font-extrabold text-outline">{ch.number}</span>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-6 tracking-tight">{ch.title}</h3>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{ch.description}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch md:h-[30rem]">
        <Reveal className="md:col-span-7 h-full" delay={0.1}>
          <motion.div whileHover={{ scale: 0.985 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="group relative rounded-2xl overflow-hidden h-72 md:h-full glow-border">
            <video
              src="/assets/mg-road-sunscreen.mp4"
              poster="/assets/mg-road-sunscreen-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              data-testid="about-featured-video"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <span data-testid="about-badge-activations" className="absolute top-4 left-4 z-10 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white px-3.5 py-1.5 rounded-full shadow-lg shadow-blue-900/40" style={{background: "linear-gradient(100deg,#3B82F6,#A855F7)"}}>Activations</span>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 inset-x-0 p-5">
              <h3 className="font-display text-lg font-bold text-white leading-snug">MG ROAD</h3>
              <div className="mt-2 flex items-center justify-between text-xs text-zinc-300">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-indigo-400" />Bangalore</span>
                <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-purple-400" />4.31M Views</span>
              </div>
            </div>
          </motion.div>
        </Reveal>
        <div className="md:col-span-5 grid grid-rows-2 gap-6 md:h-full">
          <Reveal delay={0.2}>
            <motion.div whileHover={{ scale: 0.97 }} transition={{ duration: 0.6 }} className="relative rounded-2xl overflow-hidden h-40 md:h-full min-h-[10rem] glow-border">
              <video
                src="/assets/indira-nagar.mp4"
                poster="/assets/indira-nagar-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                data-testid="about-giveaways-video"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              <span data-testid="about-badge-giveaways" className="absolute top-3 left-3 z-10 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white px-3.5 py-1.5 rounded-full shadow-lg shadow-blue-900/40" style={{background: "linear-gradient(100deg,#3B82F6,#A855F7)"}}>Giveaways</span>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 p-4">
                <h3 className="font-display text-base font-bold text-white leading-snug">INDIRA NAGAR</h3>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-300">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-indigo-400" />Bangalore</span>
                  <span className="inline-flex items-center gap-1.5"><Eye className="w-3 h-3 text-purple-400" />2.1M Views</span>
                </div>
              </div>
            </motion.div>
          </Reveal>
          <Reveal delay={0.3}>
            <motion.div whileHover={{ scale: 0.97 }} transition={{ duration: 0.6 }} className="relative rounded-2xl overflow-hidden h-40 md:h-full min-h-[10rem] glow-border">
              <video
                src="/assets/shivajinagar.mp4"
                poster="/assets/shivajinagar-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                data-testid="about-production-video"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              <span data-testid="about-badge-production" className="absolute top-3 left-3 z-10 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white px-3.5 py-1.5 rounded-full shadow-lg shadow-blue-900/40" style={{background: "linear-gradient(100deg,#3B82F6,#A855F7)"}}>Production</span>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 p-4">
                <h3 className="font-display text-base font-bold text-white leading-snug">SHIVAJINAGAR</h3>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-300">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-indigo-400" />Bangalore</span>
                  <span className="inline-flex items-center gap-1.5"><Eye className="w-3 h-3 text-purple-400" />1.2M Views</span>
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default About;
